import type { Dict } from '@aviata/platform-types';

import { dictEntity } from '../../entities';
import { dateUtils } from '../../shared/lib';
import type { SearchParameters } from './types';

const buildRangeText = (dep: string, arr: string) => `${dep} – ${arr}`;
const buildDirectionText = (dep: Dict.Cities.City, arr: Dict.Cities.City) =>
  buildRangeText(dep.name, arr.name);

export const getDirectionText = (
  searchParams: SearchParameters,
  dict: Dict.Entity,
) => {
  const { OW, RT, flights } = searchParams;
  const [firstFlight] = flights;
  const depCity = dictEntity.lib.getCity(firstFlight.orig[0], dict);

  if (OW || RT) {
    const arrCity = dictEntity.lib.getCity(firstFlight.dest[0], dict);
    return buildDirectionText(depCity, arrCity);
  }

  const [lastFlight] = flights.slice(-1);
  const arrCity = dictEntity.lib.getCity(lastFlight.dest[0], dict);
  return buildDirectionText(depCity, arrCity);
};

export const getDatesText = (searchParams: SearchParameters) => {
  const { OW, RT, flights } = searchParams;
  const [firstFlight] = flights;

  const depDate = dateUtils.getVerbalDate(firstFlight.dep_at);

  if (OW) {
    return depDate;
  }

  if (RT) {
    const arrDate = dateUtils.getVerbalDate(searchParams.flights[1].dep_at);
    return buildRangeText(depDate, arrDate);
  }

  // Multi-trip (multi-city) search: display each leg date.
  return flights
    .map(flight => dateUtils.getVerbalDate(flight.dep_at))
    .join(' – ');
};

export const getExtendedDatesText = (searchParams: SearchParameters) => {
  const { OW, RT, flights } = searchParams;
  const [firstFlight] = flights;

  const formatExtendedDate = (date: string) => {
    const day = dateUtils.format(date, 'D');
    const weekDay = dateUtils.getPrettyDayOfWeek(date);
    const month = dateUtils
      .format(date, 'MMM')
      .slice(0, 3)
      .replace('май', 'мая');
    return { day, weekDay, month, full: `${day} (${weekDay}) ${month}` };
  };

  const depDate = formatExtendedDate(firstFlight.dep_at);

  if (OW) {
    return depDate.full;
  }

  if (RT) {
    const arrDate = formatExtendedDate(searchParams.flights[1].dep_at);

    if (depDate.month === arrDate.month) {
      return `${depDate.day} (${depDate.weekDay}) - ${arrDate.day} (${arrDate.weekDay}) ${depDate.month}`;
    }

    return `${depDate.full} - ${arrDate.full}`;
  }

  // Multi-trip (multi-city): extended label per leg departure (en dash matches getDatesText).
  const legParts = flights.map(
    flight => formatExtendedDate(flight.dep_at).full,
  );
  return legParts.join(' – ');
};
